import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * Portal-rendered confirmation dialog.
 *
 * Props:
 *   open         (bool)     — show/hide
 *   title        (string)
 *   message      (string)
 *   tone         ("primary" | "danger" | "warning") — affects the confirm button color
 *   confirmLabel (string)
 *   needsNote    (bool)     — if true, shows a textarea; passed back as `note`
 *   noteLabel    (string)
 *   needsPassword (bool)    — if true, shows a password input; passed back as `password`
 *   onConfirm({ note, password }) async
 *   onCancel()
 */
export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  message = "",
  tone = "primary",
  confirmLabel = "Confirm",
  needsNote = false,
  noteLabel = "Note (optional)",
  needsPassword = false,
  onConfirm,
  onCancel,
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");

  // Reset local state whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setBusy(false);
      setNote("");
      setPassword("");
    }
  }, [open]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape closes (only when not busy).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape" && !busy) onCancel && onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (needsPassword && (!password || password.length < 6)) {
      // simple inline validation — keep dialog open
      return;
    }
    setBusy(true);
    try {
      await onConfirm({ note, password });
    } finally {
      setBusy(false);
    }
  };

  const confirmCls = {
    primary: "bg-rose-600 hover:bg-rose-500 text-white",
    danger:  "bg-red-600 hover:bg-red-500 text-white",
    warning: "bg-amber-600 hover:bg-amber-500 text-white",
  }[tone] || "bg-rose-600 hover:bg-rose-500 text-white";

  return createPortal(
    <div
      // Portal sits at the root of <body> so no parent stacking context can clip it.
      // z-index well above tag dropdown (z-[60]) and any sticky header.
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      data-testid="confirm-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => !busy && onCancel && onCancel()}
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-xl border border-white/10 bg-zinc-950/98 shadow-2xl ring-1 ring-rose-900/40 p-6"
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${
            tone === "danger" ? "bg-red-500/15 text-red-300" :
            tone === "warning" ? "bg-amber-500/15 text-amber-300" :
            "bg-rose-500/15 text-rose-300"
          }`}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              id="confirm-dialog-title"
              className="text-white font-display text-lg font-semibold"
              data-testid="confirm-dialog-title"
            >
              {title}
            </h3>
            {message && (
              <p className="text-zinc-300 text-sm mt-1 leading-relaxed" data-testid="confirm-dialog-message">
                {message}
              </p>
            )}
          </div>
        </div>

        {needsNote && (
          <div className="mt-4">
            <Label className="text-zinc-300 text-xs uppercase tracking-wider">{noteLabel}</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="mt-1.5 w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-zinc-100 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500/50"
              data-testid="confirm-dialog-note"
              autoFocus
            />
          </div>
        )}

        {needsPassword && (
          <div className="mt-4">
            <Label className="text-zinc-300 text-xs uppercase tracking-wider">Temporary password (≥6 chars)</Label>
            <Input
              type="text"
              minLength={6}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 bg-black/40 border-white/10 text-white font-mono"
              data-testid="confirm-dialog-password"
              autoFocus
              placeholder="Pick a strong temp password"
            />
            <p className="text-[11px] text-zinc-500 mt-1">User will be forced to change this on next login.</p>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => !busy && onCancel && onCancel()}
            disabled={busy}
            className="border-white/15 text-zinc-300"
            data-testid="confirm-dialog-cancel"
          >
            No
          </Button>
          <Button
            type="submit"
            disabled={busy || (needsPassword && password.length < 6)}
            className={confirmCls}
            data-testid="confirm-dialog-confirm"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</>
            ) : (
              <>Yes, {confirmLabel}</>
            )}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
